import { Error } from "@progress/kendo-react-labels";
import { GridLayoutItem } from "@progress/kendo-react-layout";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useCallback, useEffect } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConditionBuilderContext } from "@/components/ConditionBuilder/context/ConditionBuilderContext";
import { useConditionBuilder } from "@/components/ConditionBuilder/hooks/useConditionBuilder";
import { ConditionAttributeOption } from "@/components/ConditionBuilder/types";
import { getOptionLabel } from "@/components/ConditionBuilder/utils/optionLabel";
import { FormDropDownList } from "@/components/Forms";
import HierarchicalAttributeDropdown from "./HierarchicalAttributeDropdown";

interface ConditionAttributeFieldProps {
  fieldName: string;
}

const ConditionAttributeField: React.FC<ConditionAttributeFieldProps> = ({
  fieldName,
}) => {
  const { t } = useTranslation();
  const { config } = useConditionBuilderContext();
  const { valueGetter, onChange } = useConditionBuilder();
  const { fetchAttributes } = config;

  // Every row that exists in the array must be fully populated (attribute +
  // operator + value). To skip server rules entirely the user can remove the
  // row via the trash icon — the form advances on an empty array. A previous
  // "row started" gating skipped Required errors on the seeded empty row,
  // which hid the validation feedback the user expected on Next.
  const attributeValidator = useCallback(
    (value: ConditionAttributeOption | undefined) =>
      !value?.id ? t("validation.required") : undefined,
    [t],
  );

  const { data: fetchedAttributes = [] } = useQuery({
    queryKey: [fetchAttributes?.method.name, fetchAttributes?.params],
    queryFn: () =>
      fetchAttributes!
        .method(fetchAttributes!.params ?? ({} as never))
        .then(r => r.map(fetchAttributes!.parseOption)),
    enabled: !!fetchAttributes,
  });

  // fetchAttributes takes priority over the static attributes array
  const allAttributes = fetchAttributes
    ? fetchedAttributes
    : (config.attributes ?? []);

  const isHierarchical = allAttributes.some(
    (a: ConditionAttributeOption) => a.fetchSubAttributes || a.subAttributes,
  );

  if (isHierarchical) {
    return (
      <HierarchicalAttributeField
        fieldName={fieldName}
        attributes={allAttributes}
        defaultLabel={config.labels?.attribute ?? "Attribute"}
        disabled={config.readOnly}
        valueGetter={valueGetter}
        onChange={onChange}
        validator={attributeValidator}
      />
    );
  }

  // flat mode
  return (
    <GridLayoutItem>
      <FormDropDownList
        name={fieldName}
        data={allAttributes}
        label={config.labels?.attribute ?? "Attribute"}
        floatingLabel={true}
        disabled={config.readOnly}
        disableAutoClear={true}
        validator={(value: unknown) =>
          attributeValidator(value as ConditionAttributeOption | undefined)
        }
      />
    </GridLayoutItem>
  );
};

interface HierarchicalAttributeFieldProps {
  fieldName: string;
  attributes: ConditionAttributeOption[];
  defaultLabel: string;
  disabled?: boolean;
  valueGetter: (name: string) => unknown;
  onChange: (name: string, payload: { value: unknown }) => void;
  validator: (
    value: ConditionAttributeOption | undefined,
  ) => string | undefined;
}

// Hierarchical mode uses a non-Kendo custom dropdown, so we register the
// validator via useController for fieldState subscription AND explicitly via
// methods.register so the validate rule lands in RHF's validation registry
// reliably (useController alone has, in some setups, silently dropped the
// rules for deeply-nested array paths like `serverRules.0.attribute`).
const HierarchicalAttributeField: React.FC<HierarchicalAttributeFieldProps> = ({
  fieldName,
  attributes,
  defaultLabel,
  disabled,
  valueGetter,
  onChange,
  validator,
}) => {
  const methods = useFormContext();
  const { fieldState } = useController({
    name: fieldName,
    rules: {
      validate: (v: unknown) => {
        const msg = validator(v as ConditionAttributeOption | undefined);
        return typeof msg === "string" && msg.length > 0 ? msg : true;
      },
    },
  });

  useEffect(() => {
    methods.register(fieldName as never, {
      validate: (v: unknown) => {
        const msg = validator(v as ConditionAttributeOption | undefined);
        return typeof msg === "string" && msg.length > 0 ? msg : true;
      },
    });
  }, [methods, fieldName, validator]);

  const currentValue = valueGetter(fieldName) as
    | ConditionAttributeOption
    | undefined;
  const parentGroup = currentValue?.type
    ? attributes.find(a => a.id === currentValue.type)
    : undefined;
  const derivedLabel = parentGroup ? getOptionLabel(parentGroup) : defaultLabel;

  return (
    <GridLayoutItem>
      <HierarchicalAttributeDropdown
        value={currentValue}
        onChange={item => onChange(fieldName, { value: item })}
        attributes={attributes}
        label={derivedLabel}
        disabled={disabled}
      />
      {fieldState.error?.message ? (
        <Error>{fieldState.error.message}</Error>
      ) : null}
    </GridLayoutItem>
  );
};

export default ConditionAttributeField;
